/**
 * Company: SPHMedia
 * Description: [CTA below follow topics section]
 */

import { isClientSide } from "@helper/utils";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { LightboxHandle } from "@hooks/useMySPHLightbox";
import cx from "classnames";
import { ReactElement, useCallback } from "react";

export type ViewFeedCTAProps = {
  isLoggedIn: boolean;
  followedIds: string[];
  selectedIds: string[];
  lightbox: LightboxHandle | null;
  variant?: "page" | "widget";
  bhCustomEvent?: ReturnType<typeof useBHCustomEventAnalyticsCall>;
  userKeywords?: string;
};

/**
 * ViewFeedCTA
 * Description: [CTA below follow tags block shows the number of topics followed/selected and a button to view feed or log in]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function ViewFeedCTA({
  isLoggedIn,
  followedIds,
  selectedIds,
  lightbox,
  variant = "page",
  bhCustomEvent,
  userKeywords,
}: ViewFeedCTAProps): ReactElement {
  const pageReload = useCallback(() => {
    if (isClientSide) {
      bhCustomEvent?.(
        "mybh",
        "click",
        "view feed",
        undefined,
        undefined,
        undefined,
        undefined,
        userKeywords,
      );
      const url = `${window.location.pathname}?tab=pilihanku`;
      window.location.assign(url);
    }
  }, [bhCustomEvent, userKeywords]);

  const isWidgetVariant = variant === "widget";

  const styles = {
    container: cx(
      "flex flex-col items-center",
      isWidgetVariant ? "gap-1.5" : "gap-2",
    ),
    text: cx(
      "text-xxs leading-[150%] font-regular",
      isWidgetVariant ? "text-text-inverse" : "text-text-body ",
    ),
    button: cx(
      "h-[40px] font-semibold leading-[120%] flex items-center px-6 py-3 text-xs",
      isWidgetVariant
        ? "text-brand-primary bg-bg-primary"
        : "text-text-inverse bg-brand-primary",
    ),
  };

  const message = isLoggedIn
    ? `${followedIds.length} topik telah diikuti`
    : `${selectedIds.length} topik telah dipilih`;

  return (
    <>
      {(isLoggedIn ? followedIds.length > 0 : selectedIds.length > 0) && (
        <div data-testid="view-feed-cta" className={styles.container}>
          <span data-testid="view-feed-cta-text" className={styles.text}>
            {message}
          </span>

          {isLoggedIn ? (
            <button
              className={styles.button}
              onClick={pageReload}
              data-testid="view-feed-cta-button"
            >
              Lihat menu artikel
            </button>
          ) : (
            <button
              className={styles.button}
              onClick={() => {
                lightbox?.initiateLogin();
                bhCustomEvent?.("mybh", "click", "log in to follow");
              }}
              data-testid="login-cta-button"
            >
              Log masuk untuk ikuti
            </button>
          )}
        </div>
      )}
    </>
  );
}
